/* eslint-disable eslint-comments/disable-enable-pair */
import { type WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { formatTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import {
  type ClipboardScorePolicy,
  type ScoreFreezeReason,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { DEFAULT_CLOCK_IN_BONUS_POINTS } from "@src/appV2/ClipboardScore/constants";
import { addMinutes, differenceInMinutes, isBefore } from "date-fns";
import { startCase } from "lodash";

export interface GetClockInSuccessDescriptionParams {
  hasWorkerClockedInOnTime: boolean;
  isSecondShiftInDouble: boolean;
  earliestGeofenceEntryTime?: Date;
  targetArrivalTime?: Date;
  shiftStartDate?: Date;
  shiftBookedAt?: Date;
  clockInTime?: Date;
  timezone?: string;
  isTimesheetEditDisabled: boolean;
  shouldEnforceNfcTimesheetBounds: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  policy?: ClipboardScorePolicy;
  workerStage: WorkerStage;
  workerSubstage?: string;
  isClipboardScoreFreezeEnabled: boolean;
  clipboardScoreFreezeReason?: ScoreFreezeReason;
}

/**
 * Determines the appropriate description message for clock in success based on timing and shift context.
 *
 * @param params - Object containing timing and shift context information
 * @returns The success description message or undefined if no special message should be shown
 */

export function getClockInSuccessDescription(
  params: GetClockInSuccessDescriptionParams
): string | undefined {
  const {
    hasWorkerClockedInOnTime,
    isSecondShiftInDouble,
    earliestGeofenceEntryTime,
    targetArrivalTime,
    shiftStartDate,
    shiftBookedAt,
    clockInTime,
    timezone,
    isTimesheetEditDisabled,
    shouldEnforceNfcTimesheetBounds,
    isArrivalTimeFeatureEnabled,
    policy,
    workerStage,
    workerSubstage,
    isClipboardScoreFreezeEnabled,
    clipboardScoreFreezeReason,
  } = params;

  if (isClipboardScoreFreezeEnabled) {
    if (clipboardScoreFreezeReason === "WORKER_STATUS") {
      let displayStage: string = startCase(workerStage.toLowerCase());
      if (isDefined(workerSubstage) && workerSubstage === "INVITE_REQUIRED") {
        displayStage = "Invite-Required";
      }

      return `Your account is ${displayStage}, so you won't earn or lose points related to your arrival time.`;
    }

    return undefined;
  }

  if (isSecondShiftInDouble) {
    return `Since this is the second shift in a double, you won't earn or lose points related to your arrival time.`;
  }

  if (!isDefined(shiftStartDate) || !isDefined(clockInTime)) {
    return undefined;
  }

  // Use points from policy if available, otherwise fall back to default
  const clockInBonusPoints = policy?.shiftClockInConfig?.points ?? DEFAULT_CLOCK_IN_BONUS_POINTS;

  // Use grace period from policy if available, otherwise fall back to 5 minutes
  const gracePeriodMinutes = Math.abs(
    policy?.shiftClockInConfig?.requiredClockInMinutesBeforeShift ?? 5
  );

  // Find the smallest 'startFrom' value in latenessConfig to determine when penalties begin
  // Default to 15 minutes if no latenessConfig is available
  const penaltyStartMinutes =
    policy?.latenessConfig && policy.latenessConfig.length > 0
      ? Math.min(...policy.latenessConfig.map((config) => config.minutesLateRange.startFrom))
      : 15;

  const hasNFCEnabled = isTimesheetEditDisabled || shouldEnforceNfcTimesheetBounds;

  const shouldUseTargetArrivalTime =
    isDefined(targetArrivalTime) &&
    isArrivalTimeFeatureEnabled &&
    shiftStartDate < targetArrivalTime;

  const evaluationDate = shouldUseTargetArrivalTime ? targetArrivalTime : shiftStartDate;

  // if the geofence entry is within grace period after the evaluationDate or earlier
  const hasGeoFenceEntryWithinGracePeriod =
    isDefined(earliestGeofenceEntryTime) &&
    isBefore(earliestGeofenceEntryTime, addMinutes(evaluationDate, gracePeriodMinutes));

  const minutesLateBasedOnEvaluationDate = differenceInMinutes(clockInTime, evaluationDate);

  if (hasWorkerClockedInOnTime || minutesLateBasedOnEvaluationDate <= 0) {
    return `You'll earn +${clockInBonusPoints} Clipboard Score at the end of the shift for clocking in on time!`;
  }

  const shiftLeadTimeInHours = isDefined(shiftBookedAt)
    ? differenceInMinutes(shiftStartDate, shiftBookedAt) / 60
    : undefined;

  if (
    isDefined(shiftLeadTimeInHours) &&
    shiftLeadTimeInHours <= 4 &&
    !isArrivalTimeFeatureEnabled
  ) {
    const minutesLateString =
      minutesLateBasedOnEvaluationDate > 0 ? ` ${minutesLateBasedOnEvaluationDate} minutes` : "";

    return `You clocked in${minutesLateString} after the shift started, but since you booked the shift within 4 hours of the start time, your Clipboard Score won't be impacted.`;
  }

  if (shouldUseTargetArrivalTime) {
    if (hasGeoFenceEntryWithinGracePeriod) {
      return hasNFCEnabled
        ? "You arrived on time but clocked in late. If this was a mistake, get workplace approval to submit corrected times after the shift."
        : `You clocked in late, but arrived on time. You'll still earn +${clockInBonusPoints} Clipboard Score at the end of the shift.`;
    }

    // use grace period from policy (default 5) to penalty start (default 15)
    if (
      minutesLateBasedOnEvaluationDate > gracePeriodMinutes &&
      minutesLateBasedOnEvaluationDate < penaltyStartMinutes
    ) {
      return `This was ${minutesLateBasedOnEvaluationDate} min past your ${formatTime(
        targetArrivalTime,
        timezone
      )} target arrival time. You missed out on +${clockInBonusPoints} extra points, but you won't receive a penalty`;
    }

    if (minutesLateBasedOnEvaluationDate >= penaltyStartMinutes) {
      return `This was ${minutesLateBasedOnEvaluationDate} min past your ${formatTime(
        targetArrivalTime,
        timezone
      )} target arrival time. Your Clipboard Score won't change until the end of the shift.`;
    }

    return undefined;
  }

  // if we're here - we are not using TAT
  if (hasGeoFenceEntryWithinGracePeriod) {
    return hasNFCEnabled
      ? "You arrived on time but clocked in late. If this was a mistake, get workplace approval to submit corrected times after the shift."
      : `You clocked in late, but arrived on time. You'll still earn +${clockInBonusPoints} Clipboard Score at the end of the shift.`;
  }

  if (
    minutesLateBasedOnEvaluationDate > gracePeriodMinutes &&
    minutesLateBasedOnEvaluationDate < penaltyStartMinutes
  ) {
    return `This was ${minutesLateBasedOnEvaluationDate} min past the shift's start time. You missed out on +${clockInBonusPoints} extra points, but you won't receive a penalty`;
  }

  if (minutesLateBasedOnEvaluationDate >= penaltyStartMinutes) {
    return `This was ${minutesLateBasedOnEvaluationDate} min past the shift's start time. Your Clipboard Score won't change until the end of the shift.`;
  }

  return undefined;
}
